/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Titulo,
  Boton,
  Formulario,
  Sidebar,
  useIsMobile,
  TablaFiltrable
} from "../../shared";

import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

export default function Facturacion() {
  const [pagos, setPagos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  const [sidebarFactura, setSidebarFactura] = useState(false);
  const [loaderFactura, setLoaderFactura] = useState(false);
  const [formKeyFactura, setFormKeyFactura] = useState(0);
  // Ids de pagos tildados en el formulario de factura (para el total en vivo).
  const [seleccionados, setSeleccionados] = useState([]);
  const [facturaMenuSeleccionada, setFacturaMenuSeleccionada] = useState(null);
  const [descargandoFacturaId, setDescargandoFacturaId] = useState(null);
  const menuFacturaRef = useRef(null);
  const toast = useRef(null);

  function cargarDatos() {
    accessAPI(
      "GET",
      "colab/facturacion/pagos",
      null,
      (respuesta) => {
        setPagos(respuesta || []);
        setLoading(false);
      },
      () => setLoading(false)
    );
    accessAPI(
      "GET",
      "colab/facturacion/facturas",
      null,
      (respuesta) => setFacturas(respuesta || []),
      () => {}
    );
  }

  useEffect(() => {
    cargarDatos();
  }, []);

  // Descarga (factura o comprobante) mostrando un loader en la fila.
  function descargarDeFactura(f, tipo) {
    const esComprobante = tipo === "comprobante";
    setDescargandoFacturaId(f.id);
    downloadFile(
      `colab/facturacion/factura/${f.id}/${esComprobante ? "comprobante" : "archivo"}`,
      (esComprobante ? f.comprobante?.nombredescarga : f.archivo?.nombredescarga) ||
        (esComprobante ? "comprobante" : "factura"),
      () => setDescargandoFacturaId(null),
      () => {
        setDescargandoFacturaId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: esComprobante
            ? "No se pudo descargar el comprobante."
            : "No se pudo descargar la factura.",
          sticky: true
        });
      }
    );
  }

  // Opciones del menú contextual de una factura del histórico.
  function getMenuFacturaItems() {
    const f = facturaMenuSeleccionada;
    if (!f) return [];
    const items = [
      {
        label: "Descargar factura",
        icon: "pi pi-download",
        command: () => descargarDeFactura(f, "archivo")
      }
    ];
    if (f.comprobanteArchivoId) {
      items.push({
        label: "Descargar comprobante de pago",
        icon: "pi pi-download",
        command: () => descargarDeFactura(f, "comprobante")
      });
    }
    if (f.estado !== "pagada") {
      items.push({
        label: "Anular factura",
        icon: "pi pi-times",
        command: () => anularFactura(f)
      });
    }
    return items;
  }

  function anularFactura(f) {
    confirmDialog({
      message:
        "¿Anular esta factura? Se eliminará la factura y sus actividades volverán a pagos pendientes.",
      header: "Confirmar",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí, anular",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `colab/facturacion/factura/${f.id}`,
          null,
          () => {
            cargarDatos();
            toast.current?.show({
              severity: "success",
              summary: "Factura anulada",
              detail: "Las actividades volvieron a pagos pendientes.",
              life: 3000
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta?.[0]?.msg || "No se pudo anular la factura.",
              sticky: true
            });
          }
        );
      }
    });
  }

  // Pendientes de facturar = ni pagados ni ya incluidos en una factura.
  const pendientes = pagos.filter(
    (p) => p.estado !== "pagado" && !p.facturaColaboradorId
  );

  // Columnas compartidas por las dos tablas
  const procesoBody = (row) => {
    const proc = row.actividadPaso?.actividad?.proceso;
    if (!proc) return "-";
    return (
      <div>
        <div css={{ fontWeight: 600 }}>{proc.nombre}</div>
        {proc.nombrefantasia && (
          <div css={{ fontSize: "0.85em", color: "var(--grisTextos)" }}>
            {proc.nombrefantasia}
          </div>
        )}
      </div>
    );
  };

  const pasoBody = (row) => {
    const act = row.actividadPaso?.actividad;
    const paso = row.actividadPaso;
    return (
      <div>
        <div>{act?.actividadtipo?.nombre || "-"}</div>
        {paso?.nombre && (
          <div css={{ fontSize: "0.85em", color: "var(--grisTextos)" }}>
            {paso.nombre}
          </div>
        )}
      </div>
    );
  };

  const rolBody = (row) => (
    <Tag
      severity={row.rol === "decisor" ? "info" : "warning"}
      css={{ fontSize: "0.75rem" }}
    >
      {row.rol === "decisor" ? "Decisor" : "En formación"}
    </Tag>
  );

  const montoBody = (row) => `${row.moneda?.simbolo || ""} ${row.monto}`.trim();

  // Etiqueta legible de un pago para el checkbox del formulario de factura.
  const descripcionPago = (p) => {
    const proc = p.actividadPaso?.actividad?.proceso;
    const act = p.actividadPaso?.actividad?.actividadtipo?.nombre;
    const paso = p.actividadPaso?.nombre;
    const rol = p.rol === "decisor" ? "Decisor" : "En formación";
    const montoTxt = `${p.moneda?.simbolo || ""} ${p.monto}`.trim();
    return `${proc?.nombre || "Proceso"}${act ? " · " + act : ""}${
      paso ? " / " + paso : ""
    } — ${montoTxt} · ${rol}`;
  };

  // Formulario dinámico: filepicker + un checkbox (tildado) por pago pendiente.
  const formConfigFactura = {
    secciones: [
      {
        campos: [
          {
            nombre: "archivo",
            tipoitemformularioid: 10,
            etiqueta: "Archivo de la factura",
          },
          ...pendientes.map((p) => ({
            nombre: `pago_${p.id}`,
            tipoitemformularioid: 6,
            etiqueta: descripcionPago(p),
            respuesta: true,
          })),
          {
            nombre: "notas",
            tipoitemformularioid: 11,
            etiqueta: "Notas",
          },
        ],
      },
    ],
    botones: [
      {
        // validar: true es necesario para que Formulario pase el archivo como
        // segundo parámetro del callback (sin validar no lo entrega). Ningún
        // campo es `requerido`, así que la validación siempre pasa; el archivo y
        // la selección se chequean manualmente en submitFactura.
        etiqueta: "Subir factura",
        callback: submitFactura,
        validar: true,
      },
    ],
  };

  function submitFactura(campos, archivo) {
    const pagoIds = Object.keys(campos)
      .filter((k) => k.startsWith("pago_") && campos[k])
      .map((k) => parseInt(k.slice("pago_".length)));

    if (!archivo) {
      toast.current?.show({
        severity: "warn",
        summary: "Falta el archivo",
        detail: "Adjuntá el archivo de la factura.",
        life: 3000,
      });
      return;
    }
    if (!pagoIds.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Sin pagos",
        detail: "Seleccioná al menos un pago pendiente.",
        life: 3000,
      });
      return;
    }

    setLoaderFactura(true);
    const datos = { pagoIds };
    if (campos.notas?.trim()) {
      datos.notas = campos.notas.trim();
    }
    accessAPI(
      "POST",
      "colab/facturacion/factura",
      datos,
      () => {
        setLoaderFactura(false);
        setSidebarFactura(false);
        cargarDatos();
        toast.current?.show({
          severity: "success",
          summary: "Factura subida",
          detail: "La factura se registró correctamente.",
          life: 3000,
        });
      },
      (respuesta) => {
        setLoaderFactura(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo subir la factura.",
          sticky: true,
        });
      },
      archivo
    );
  }

  // Deriva los pagos tildados desde el contenido del Formulario y actualiza el
  // total en vivo.
  function handleCambioFactura(contenido) {
    setSeleccionados(
      pendientes.filter((p) => contenido[`pago_${p.id}`]).map((p) => p.id)
    );
  }

  function abrirSubirFactura() {
    // Arrancan todos tildados, igual que los checkboxes del formulario.
    setSeleccionados(pendientes.map((p) => p.id));
    setFormKeyFactura((k) => k + 1);
    setSidebarFactura(true);
  }

  // Total en vivo de lo tildado, agrupado por moneda (una factura debe ser de
  // una sola moneda; si hay varias, se muestran separadas como aviso).
  const totalesPorMoneda = {};
  pendientes
    .filter((p) => seleccionados.includes(p.id))
    .forEach((p) => {
      const sim = p.moneda?.simbolo || "";
      totalesPorMoneda[sim] = (totalesPorMoneda[sim] || 0) + p.monto;
    });
  const resumenTotales =
    Object.entries(totalesPorMoneda)
      .map(([sim, tot]) => `${sim} ${tot}`.trim())
      .join("  ·  ") || "—";

  if (loading) {
    return (
      <>
        <Skeleton
          width="18em"
          height="2.1em"
          css={{ marginBottom: "1.1em", marginTop: "1em" }}
        />
        <Skeleton width="100%" height="14em" css={{ marginBottom: "1.5em" }} />
        <Skeleton width="100%" height="10em" />
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={getMenuFacturaItems()} popup ref={menuFacturaRef} />

      <Sidebar
        visible={sidebarFactura}
        onHide={() => setSidebarFactura(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Subir factura</Titulo>
          {pendientes.length === 0 ? (
            <p css={{ color: "var(--grisTextos)" }}>
              No tenés pagos pendientes para facturar.
            </p>
          ) : (
            <>
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                  padding: "0.6rem 0.8rem",
                  borderRadius: "6px",
                  backgroundColor: "var(--grisFondo, #f4f5f7)"
                }}
              >
                <span css={{ color: "var(--grisTextos)" }}>Total a facturar</span>
                <span css={{ fontWeight: 700 }}>{resumenTotales}</span>
              </div>
              <Formulario
                key={formKeyFactura}
                config={formConfigFactura}
                loader={loaderFactura}
                onCambio={handleCambioFactura}
              />
            </>
          )}
        </div>
      </Sidebar>

      <Titulo
        botones={
          pendientes.length > 0 ? (
            <Boton verde callback={abrirSubirFactura}>
              Subir factura
            </Boton>
          ) : undefined
        }
      >
        Facturación
      </Titulo>

      <Card css={{ marginBottom: "1.5rem" }}>
        <Titulo>Pagos pendientes</Titulo>
        <TablaFiltrable conFiltros={false}
          value={pendientes}
          size="small"
          stripedRows
          paginator={pendientes.length > 10}
          rows={10}
          emptyMessage="No tenés pagos pendientes"
        >
          <Column header="Proceso" body={procesoBody} />
          <Column header="Actividad / Paso" body={pasoBody} />
          {!isMobile && (
            <Column header="Rol" body={rolBody} style={{ width: "9rem" }} />
          )}
          <Column
            header="Monto"
            body={montoBody}
            alignHeader="right"
            style={{ width: "9rem", textAlign: "right" }}
          />
          {!isMobile && (
            <Column
              header="Desde"
              body={(row) => formatFecha(row.fechaCreacion)}
              style={{ width: "8rem" }}
            />
          )}
        </TablaFiltrable>
      </Card>

      <Card>
        <Titulo>Histórico de pagos</Titulo>
        <TablaFiltrable conFiltros={false}
          key={`facturas-${descargandoFacturaId}`}
          value={facturas}
          size="small"
          stripedRows
          paginator={facturas.length > 10}
          rows={10}
          emptyMessage="Todavía no subiste facturas"
        >
          {!isMobile && (
            <Column
              header="Fecha"
              body={(f) => formatFecha(f.fecha)}
              style={{ width: "8rem" }}
            />
          )}
          <Column
            header="Actividades"
            body={(f) => (
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.2rem"
                }}
              >
                {(f.pagos || []).map((p) => {
                  const proc = p.actividadPaso?.actividad?.proceso;
                  const act =
                    p.actividadPaso?.actividad?.actividadtipo?.nombre;
                  const paso = p.actividadPaso?.nombre;
                  return (
                    <div key={p.id} css={{ fontSize: "0.88em" }}>
                      {proc?.nombre || "Proceso"}
                      {act ? " · " + act : ""}
                      {paso ? " / " + paso : ""}
                    </div>
                  );
                })}
              </div>
            )}
          />
          <Column
            header="Monto"
            body={(f) => `${f.moneda?.simbolo || ""} ${f.monto}`.trim()}
            alignHeader="right"
            style={{ width: "9rem", textAlign: "right" }}
          />
          <Column
            header="Estado"
            body={(f) =>
              f.estado === "pagada" ? (
                <Tag severity="success" css={{ fontSize: "0.75rem" }}>
                  Pagado
                </Tag>
              ) : (
                <Tag severity="danger" css={{ fontSize: "0.75rem" }}>
                  Pendiente
                </Tag>
              )
            }
            style={{ width: "8rem" }}
          />
          <Column
            body={(f) =>
              descargandoFacturaId === f.id ? (
                <i className="pi pi-spin pi-spinner" />
              ) : (
                <Boton
                  texto
                  negro
                  chico
                  callback={(e) => {
                    setFacturaMenuSeleccionada(f);
                    menuFacturaRef.current.toggle(e);
                  }}
                >
                  <i className="pi pi-ellipsis-v" />
                </Boton>
              )
            }
            style={{ width: "3rem", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>
    </>
  );
}
